// Phase II · Wave D.1 · react-i18next instance (Wave D-scoped · parallel).
//
// Keeps the pre-existing M14 ``I18nContext`` (vanilla React Context,
// cookie key ``v12_locale``) untouched. This instance uses:
//
//   · react-i18next + i18next + i18next-browser-languagedetector
//   · Cookie key   : ``glms_locale`` (1 yr TTL)
//   · Namespaces   : common / admin / marketplace / blog / legal / errors
//   · Fallback     : ``en-US``
//   · Lazy load    : namespace JSON files are dynamic-imported on demand
//                     via ``i18n.loadNamespaces`` so the main bundle
//                     stays slim (240 JSON files total · never shipped
//                     eagerly).
//
// Zero-touch policy — IMPORTANT:
//   We intentionally DO NOT ``.use(initReactI18next)`` on this parallel
//   instance. ``initReactI18next`` is a singleton that writes the
//   active instance into react-i18next's global default · chaining it
//   here would hijack every pre-existing ``useTranslation(ns)`` call
//   in the codebase (the M14 i18n in ``src/i18n/index.js`` holds that
//   slot). Consumers that need the Wave-D bundles must call
//   ``useTranslation("common", { i18n: glmsI18n })`` explicitly ·
//   see ``LocaleSwitcher.jsx`` for the canonical pattern.
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import {
    DEFAULT_LOCALE, GLMS_LOCALE_CODES, LOCALE_COOKIE,
} from "./glms_locales";

export const GLMS_NAMESPACES = [
    "common", "admin", "marketplace", "blog", "legal", "errors",
];

// Eager load of the EN common bundle only · everything else is lazy.
// The require() is picked up by webpack/CRA at build time so we always
// have at least the fallback dictionary available before any component
// mounts.
let _enCommon;
try {
    // eslint-disable-next-line global-require
    _enCommon = require("./locales/en-US/common.json");
} catch (e) {
    _enCommon = {};
}

export const glmsI18n = i18n.createInstance();

glmsI18n
    .use(LanguageDetector)
    .init({
        resources: {
            "en-US": { common: _enCommon },
        },
        supportedLngs: GLMS_LOCALE_CODES,
        fallbackLng: DEFAULT_LOCALE,
        defaultNS: "common",
        ns: GLMS_NAMESPACES,
        debug: false,
        interpolation: { escapeValue: false }, // React escapes by default
        detection: {
            // cookie → navigator → htmlTag per brief
            order: ["cookie", "navigator", "htmlTag"],
            caches: ["cookie"],
            lookupCookie: LOCALE_COOKIE,
            cookieMinutes: 60 * 24 * 365, // 1 yr
            cookieOptions: { path: "/", sameSite: "lax", secure: true },
        },
        react: {
            useSuspense: false, // namespaces are tiny · zero suspense fallback flashes
        },
    });

// -------- Lazy namespace loader --------
//
// On ``changeLanguage`` or ``loadNamespaces`` i18next asks us for the
// bundle. We dynamic-import the JSON lazily (one network/chunk per
// namespace per locale) · the result is cached in-memory so subsequent
// calls are free.
const _loaded = new Set(["en-US:common"]);

export async function loadGlmsBundle(locale, namespace) {
    const key = `${locale}:${namespace}`;
    if (_loaded.has(key)) return;
    try {
        // Webpack code-splits on the dynamic template literal.
        const mod = await import(
            /* webpackChunkName: "glms-i18n-[request]" */
            `./locales/${locale}/${namespace}.json`
        );
        glmsI18n.addResourceBundle(locale, namespace, mod.default || mod, true, true);
        _loaded.add(key);
    } catch (e) {
        // Missing bundle → the fallback (en-US) is already loaded.
        // Record the attempt so we don't retry on every render.
        _loaded.add(key);
    }
}

// Convenience helper used by LocaleSwitcher · ensures both the current
// locale and the configured defaultNS are available before switching.
export async function setGlmsLocale(locale) {
    await loadGlmsBundle(locale, "common");
    await glmsI18n.changeLanguage(locale);
}

export default glmsI18n;
